/* ============================================================
 * Lion Theme — lion-urgency-message.js
 *
 * <lion-urgency-message> — honest, merchant-configured shipping cutoff.
 * Computes hours/minutes until the daily cutoff time set in the
 * theme editor. After cutoff passes, shows the merchant's
 * "after cutoff" message. Updates every 30s.
 *
 * Theme Store compliant: no fabricated counters, no fake social
 * proof. Every displayed value is derived from real time + a
 * merchant-set value.
 * ============================================================ */

class UrgencyMessage extends HTMLElement {
  constructor() {
    super();
    this.enabled = this.dataset.cutoffEnabled === "true";
    this.hour = parseInt(this.dataset.cutoffHour, 10);
    this.minute = parseInt(this.dataset.cutoffMinute, 10) || 0;
    this.template = this.dataset.cutoffTemplate || "";
    this.afterCutoff = this.dataset.afterCutoff || "";
    this.textEl = this.querySelector("[data-urgency-text]");

    if (!this.enabled || !this.textEl || isNaN(this.hour)) return;

    this.tick();
    this.interval = setInterval(() => this.tick(), 30000);
  }

  disconnectedCallback() {
    if (this.interval) clearInterval(this.interval);
  }

  tick() {
    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setHours(this.hour, this.minute, 0, 0);

    if (now >= cutoff) {
      // Past cutoff for today
      if (this.afterCutoff) {
        this.textEl.textContent = this.afterCutoff;
        this.removeAttribute("data-state");
      } else {
        // No after-cutoff message: hide the block entirely
        this.setAttribute("hidden", "");
      }
      return;
    }

    const diffMs = cutoff - now;
    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const timeStr =
      hours > 0
        ? `${hours}h ${String(minutes).padStart(2, "0")}m`
        : `${minutes}m`;

    /* The template string uses [time] as a placeholder, replaced at runtime. */
    this.textEl.innerHTML = this.template.replace(
      /\[time\]/g,
      `<strong>${timeStr}</strong>`,
    );
    this.dataset.state = totalMinutes < 60 ? "urgent" : "normal";
  }
}

if (!customElements.get("lion-urgency-message")) {
  customElements.define("lion-urgency-message", UrgencyMessage);
}
